import { Fragment, useEffect, useState, type ReactNode } from "react";
import {
  createHighlighterCore,
  type HighlighterCore,
  type ThemedToken,
} from "shiki/core";
import { createOnigurumaEngine } from "shiki/engine/oniguruma";
import { useThemeContext } from "../Theme/useThemeContext";

export type CodeLanguage = "json" | "xml" | "kotlin" | "plaintext";

interface CodeBlockProps {
  children: ReactNode;
  /** Optional language for syntax highlighting. Falls back to plain mono text when omitted. */
  language?: CodeLanguage;
}

interface HighlightResult {
  tokens: ThemedToken[][];
  fg: string;
}

interface KeyedLine {
  key: string;
  tokens: ThemedToken[];
}

function buildLineKeys(tokens: ThemedToken[][]): KeyedLine[] {
  const result: KeyedLine[] = [];
  let runningOffset = 0;
  for (const line of tokens) {
    const first = line[0];
    if (first !== undefined) {
      runningOffset = first.offset;
      result.push({ key: `o${first.offset}`, tokens: line });
    } else {
      result.push({ key: `e${runningOffset}`, tokens: line });
      runningOffset += 1; // disambiguate consecutive empty lines
    }
  }
  return result;
}

const LIGHT_THEME = "github-light";
const DARK_THEME = "github-dark";

// Shiki ships ~200 language grammars. Importing from `shiki` auto-registers
// all of them and bloats the build with chunks we never load. `shiki/core`
// plus explicit dynamic imports keeps the dist clean — Vite only emits
// chunks for the four assets actually referenced below. We preload both
// light and dark themes so toggling Hubble's theme re-renders the block
// synchronously without re-initialising the WASM engine.
let highlighterPromise: Promise<HighlighterCore> | undefined;
function getHighlighter(): Promise<HighlighterCore> {
  // Clear the cached promise on rejection (e.g. transient WASM fetch
  // failure on a flaky network) so the next call retries instead of
  // silently re-using the rejected promise for the rest of the session.
  highlighterPromise ??= createHighlighterCore({
    themes: [
      import("shiki/themes/github-light.mjs"),
      import("shiki/themes/github-dark.mjs"),
    ],
    langs: [
      import("shiki/langs/json.mjs"),
      import("shiki/langs/xml.mjs"),
      import("shiki/langs/kotlin.mjs"),
    ],
    engine: createOnigurumaEngine(import("shiki/wasm")),
  }).catch((err) => {
    highlighterPromise = undefined;
    throw err;
  });
  return highlighterPromise;
}

/** Styled preformatted code block for displaying JSON, scenario code, metadata, etc. */
export function CodeBlock({ children, language }: CodeBlockProps) {
  const { resolvedTheme } = useThemeContext();
  const code = typeof children === "string" ? children : String(children ?? "");
  const wantsHighlight = language && language !== "plaintext";
  const [highlighted, setHighlighted] = useState<HighlightResult | undefined>();

  useEffect(() => {
    if (!wantsHighlight) {
      setHighlighted(undefined);
      return;
    }
    // Snapshot tests render synchronously and capture the DOM before any
    // async setState lands. If Shiki sometimes wins that race and sometimes
    // doesn't, snapshots become flaky between local and CI. Skip the
    // highlighter in test mode so the fallback `<pre>` is captured
    // deterministically — the highlighter still runs in dev and prod.
    if (import.meta.env.MODE === "test") return;
    const theme = resolvedTheme === "dark" ? DARK_THEME : LIGHT_THEME;
    let cancelled = false;
    getHighlighter()
      .then((h) => {
        if (cancelled) return;
        const result = h.codeToTokens(code, { lang: language, theme });
        setHighlighted({ tokens: result.tokens, fg: result.fg ?? "inherit" });
      })
      .catch(() => {
        // Highlighter init can fail without WASM support; we fall through
        // to the plain-text rendering below.
      });
    return () => {
      cancelled = true;
    };
  }, [code, language, wantsHighlight, resolvedTheme]);

  if (highlighted) {
    // We render Shiki's tokens as React spans rather than feeding the
    // highlighter's HTML string into dangerouslySetInnerHTML — Hubble's
    // no-dangerously-set-inner-html lint rule reserves that escape hatch
    // for EmailHtmlPreview. Background comes from `bg-foreground/5` so the
    // block matches other Hubble code surfaces; foreground colors come
    // from the active Shiki theme and adapt with light/dark mode.
    //
    // Each Shiki token carries `offset` — its character index in the source
    // — which is a stable, unique key. Empty lines have no tokens, so we
    // synthesise a running offset using the previous line's last token's
    // end position; that keeps the no-array-index-key lint rule happy.
    const lines = buildLineKeys(highlighted.tokens);
    return (
      <pre
        className="max-h-[60dvh] overflow-auto rounded-md bg-foreground/5 p-4 font-mono text-sm leading-relaxed"
        style={{ color: highlighted.fg }}
      >
        <code>
          {lines.map(({ key, tokens }) => (
            <Fragment key={key}>
              {tokens.map((token) => (
                <span key={token.offset} style={{ color: token.color }}>
                  {token.content}
                </span>
              ))}
              {"\n"}
            </Fragment>
          ))}
        </code>
      </pre>
    );
  }

  return (
    <pre className="max-h-[60dvh] overflow-auto rounded-md bg-foreground/5 p-4 font-mono text-sm leading-relaxed">
      {children}
    </pre>
  );
}
